import { useEffect, useState } from "react";
import { OwnerNav } from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Phone, Mail, Clock, X, Calendar as CalIcon } from "lucide-react";
import { toast, Toaster } from "sonner";
import { AgendaGrid } from "@/components/AgendaGrid";

function todayIso() { return new Date().toISOString().slice(0,10); }
function addDaysIso(iso, n) {
  const d = new Date(iso); d.setDate(d.getDate() + n); return d.toISOString().slice(0,10);
}
function frDate(iso) {
  return new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
}

export default function Dashboard() {
  const [appts, setAppts] = useState([]);
  const [services, setServices] = useState([]);
  const [salon, setSalon] = useState(null);
  const [staff, setStaff] = useState([]);
  const [staffFilter, setStaffFilter] = useState("all");
  const [viewDate, setViewDate] = useState(todayIso());
  const [viewMode, setViewMode] = useState("day"); // day | week
  const [openAdd, setOpenAdd] = useState(false);
  const [breaks, setBreaks] = useState([]);
  const [displayMode, setDisplayMode] = useState("agenda"); // agenda | list

  const load = async () => {
    const start = viewMode === "day" ? viewDate : viewDate;
    const end = viewMode === "day" ? viewDate : addDaysIso(viewDate, 6);
    const { data } = await api.get("/owner/appointments", { params: { start, end } });
    setAppts(data);
  };
  const loadBreaks = async () => {
    const { data } = await api.get("/owner/breaks");
    setBreaks(data);
  };

  useEffect(() => {
    (async () => {
      const [sv, st, sl] = await Promise.all([
        api.get("/owner/services"),
        api.get("/owner/staff"),
        api.get("/owner/salon"),
      ]);
      setServices((sv.data || []).filter((s) => s.active));
      setStaff((st.data || []).filter((s) => s.active));
      setSalon(sl.data);
    })();
  }, []);

  const staffColor = (id) => staff.find((s) => s.id === id)?.color || "#C5A059";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const end = viewMode === "day" ? viewDate : addDaysIso(viewDate, 6);
      const { data } = await api.get("/owner/appointments", { params: { start: viewDate, end } });
      if (!cancelled) setAppts(data);
    })();
    return () => { cancelled = true; };
  }, [viewDate, viewMode]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await api.get("/owner/breaks");
      if (!cancelled) setBreaks(data);
    })();
    return () => { cancelled = true; };
  }, []);

  const cancel = async (id) => {
    if (!window.confirm("Annuler ce rendez-vous ? Le client sera notifié par email.")) return;
    try {
      await api.patch(`/owner/appointments/${id}/cancel`);
      toast.success("Rendez-vous annulé");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleMove = async (id, patch) => {
    try {
      await api.patch(`/owner/appointments/${id}/move`, patch);
      toast.success("Rendez-vous déplacé");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const handleBlock = async (payload) => {
    try {
      await api.post("/owner/breaks", payload);
      toast.success("Créneau bloqué");
      loadBreaks();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const handleUnblock = async (id) => {
    try {
      await api.delete(`/owner/breaks/${id}`);
      toast.success("Créneau débloqué");
      loadBreaks();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const todayAppts = appts.filter(a => a.date === todayIso() && a.status === "confirmed");
  const upcoming = appts.filter(a => a.date > todayIso() && a.status === "confirmed").slice(0, 8);

  const days = viewMode === "week"
    ? Array.from({length: 7}, (_, i) => addDaysIso(viewDate, i))
    : [viewDate];

  return (
    <div className="min-h-screen bg-[#0A0908] text-[#F4F0EA]">
      <OwnerNav />
      <Toaster theme="dark" />

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-12 flex-wrap gap-4">
          <div>
            <div className="text-[10px] tracking-[0.4em] text-[#C5A059] uppercase mb-2">Planning</div>
            <h1 className="font-serif text-4xl sm:text-5xl font-light">Bonjour.</h1>
          </div>
          <AddAppointmentDialog open={openAdd} setOpen={setOpenAdd} services={services} staff={staff} onCreated={load} />
        </div>

        {/* Today summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <SummaryCard label="Aujourd'hui" value={todayAppts.length} sublabel="rendez-vous confirmés" testId="summary-today" />
          <SummaryCard label="Cette semaine" value={appts.filter(a => a.status === "confirmed").length} sublabel="dans la période affichée" testId="summary-period" />
          <SummaryCard label="Revenu estimé" value={`${appts.filter(a=>a.status==="confirmed").reduce((s,a)=>s+a.price,0)}€`} sublabel="période affichée" testId="summary-revenue" />
        </div>

        {/* View switcher */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={() => setViewDate(addDaysIso(viewDate, viewMode === "day" ? -1 : -7))} className="border border-[#2C2A26] hover:border-[#C5A059] px-3 py-2 text-xs uppercase tracking-[0.2em]" data-testid="prev-period">←</button>
            <button onClick={() => setViewDate(todayIso())} className="border border-[#2C2A26] hover:border-[#C5A059] px-4 py-2 text-xs uppercase tracking-[0.2em]" data-testid="today-btn">Aujourd&apos;hui</button>
            <button onClick={() => setViewDate(addDaysIso(viewDate, viewMode === "day" ? 1 : 7))} className="border border-[#2C2A26] hover:border-[#C5A059] px-3 py-2 text-xs uppercase tracking-[0.2em]" data-testid="next-period">→</button>
            <input type="date" value={viewDate} onChange={(e)=>setViewDate(e.target.value)} className="bg-[#141311] border border-[#2C2A26] px-3 py-2 text-sm text-[#F4F0EA]" data-testid="date-picker" />
            <select value={staffFilter} onChange={(e)=>setStaffFilter(e.target.value)} className="bg-[#141311] border border-[#2C2A26] px-3 py-2 text-sm text-[#F4F0EA]" data-testid="staff-filter">
              <option value="all">Tous les coiffeurs</option>
              {staff.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex border border-[#2C2A26]">
              <button onClick={() => setDisplayMode("agenda")} className={`px-5 py-2 text-xs uppercase tracking-[0.2em] ${displayMode==="agenda"?"bg-[#C5A059] text-[#0A0908]":"text-[#A39E93]"}`} data-testid="display-agenda">Agenda</button>
              <button onClick={() => setDisplayMode("list")} className={`px-5 py-2 text-xs uppercase tracking-[0.2em] ${displayMode==="list"?"bg-[#C5A059] text-[#0A0908]":"text-[#A39E93]"}`} data-testid="display-list">Liste</button>
            </div>
            <div className="flex border border-[#2C2A26]">
              <button onClick={() => setViewMode("day")} className={`px-5 py-2 text-xs uppercase tracking-[0.2em] ${viewMode==="day"?"bg-[#C5A059] text-[#0A0908]":"text-[#A39E93]"}`} data-testid="view-day">Jour</button>
              <button onClick={() => setViewMode("week")} className={`px-5 py-2 text-xs uppercase tracking-[0.2em] ${viewMode==="week"?"bg-[#C5A059] text-[#0A0908]":"text-[#A39E93]"}`} data-testid="view-week">Semaine</button>
            </div>
          </div>
        </div>

        {displayMode === "agenda" && (
          <div className="mb-4 text-xs text-[#6B6760]">Glissez un rendez-vous pour le déplacer · cliquez sur une case vide pour bloquer un créneau</div>
        )}

        {displayMode === "agenda" ? (
          <AgendaGrid viewMode={viewMode} viewDate={viewDate} staff={staff} staffFilter={staffFilter} appts={appts} breaks={breaks} onMove={handleMove} onBlock={handleBlock} onUnblock={handleUnblock} onCancel={cancel} />
        ) : (
        /* Days list */
        <div className="space-y-10" data-testid="planning-view">
          {days.map((d) => {
            const dayAppts = appts.filter(a => a.date === d && (staffFilter === "all" || a.staff_id === staffFilter)).sort((a,b) => a.start_time.localeCompare(b.start_time));
            return (
              <div key={d}>
                <div className="flex items-baseline gap-3 mb-4">
                  <h3 className="font-serif text-2xl font-light capitalize">{frDate(d)}</h3>
                  <span className="text-xs uppercase tracking-[0.2em] text-[#6B6760]">{dayAppts.filter(a=>a.status==="confirmed").length} rdv</span>
                </div>
                {dayAppts.length === 0 ? (
                  <div className="border border-dashed border-[#2C2A26] p-6 text-[#6B6760] text-sm">Aucun rendez-vous.</div>
                ) : (
                  <div className="divide-y divide-[#2C2A26] border-y border-[#2C2A26]">
                    {dayAppts.map((a) => (
                      <div key={a.id} className={`grid grid-cols-12 gap-4 py-4 items-center ${a.status==="cancelled" ? "opacity-40" : ""}`} data-testid={`appt-row-${a.id}`}>
                        <div className="col-span-2 sm:col-span-1 font-serif text-xl text-[#C5A059]">{a.start_time}</div>
                        <div className="col-span-1 hidden sm:block text-[#6B6760] text-xs uppercase tracking-[0.15em]">{a.duration}min</div>
                        <div className="col-span-6 sm:col-span-5">
                          <div className="font-medium flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full inline-block shrink-0" style={{ backgroundColor: staffColor(a.staff_id) }} title={a.staff_name} />
                            {a.client_first_name} {a.client_last_name}
                          </div>
                          <div className="text-xs text-[#A39E93]">{a.service_name}{a.staff_name ? ` · ${a.staff_name}` : ""}</div>
                        </div>
                        <div className="col-span-2 hidden sm:flex flex-col text-xs text-[#A39E93]">
                          <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{a.client_phone}</span>
                          {a.client_email && <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3" />{a.client_email}</span>}
                        </div>
                        <div className="col-span-3 sm:col-span-2 flex items-center justify-end gap-3">
                          {a.source === "manual" && <span className="text-[9px] uppercase tracking-[0.2em] text-[#6B6760] border border-[#2C2A26] px-2 py-0.5">Manuel</span>}
                          {a.status === "confirmed" && (
                            <button onClick={() => cancel(a.id)} className="text-[#A39E93] hover:text-[#8C3131] text-xs uppercase tracking-[0.2em]" data-testid={`cancel-${a.id}`}>
                              <X className="w-4 h-4" />
                            </button>
                          )}
                          {a.status === "cancelled" && <span className="text-[9px] uppercase tracking-[0.2em] text-[#8C3131]">Annulé</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, sublabel, testId }) {
  return (
    <div className="bg-[#141311] border border-[#2C2A26] p-6" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#A39E93] mb-3">{label}</div>
      <div className="font-serif text-5xl font-light text-[#F4F0EA]">{value}</div>
      <div className="text-xs text-[#6B6760] mt-2">{sublabel}</div>
    </div>
  );
}

function AddAppointmentDialog({ open, setOpen, services, staff, onCreated }) {
  const [form, setForm] = useState({
    service_id: "", staff_id: "", date: todayIso(), start_time: "09:00",
    client_first_name: "", client_last_name: "", client_phone: "", client_email: "", internal_note: "",
  });
  const [saving, setSaving] = useState(false);

  const reset = () => setForm({ service_id: "", staff_id: "", date: todayIso(), start_time: "09:00", client_first_name: "", client_last_name: "", client_phone: "", client_email: "", internal_note: "" });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.client_email) delete payload.client_email;
      if (!payload.staff_id) delete payload.staff_id;
      await api.post("/owner/appointments", payload);
      toast.success("Rendez-vous créé");
      onCreated();
      reset();
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="btn-primary" data-testid="add-appt-btn">
          <Plus className="w-4 h-4" strokeWidth={2} /> Nouveau RDV
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#141311] border-[#2C2A26] text-[#F4F0EA] max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl font-light">Ajouter un rendez-vous</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="add-appt-form">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Prestation</Label>
            <Select value={form.service_id} onValueChange={(v) => setForm({...form, service_id: v})}>
              <SelectTrigger className="mt-2 bg-[#0A0908] border-[#2C2A26]" data-testid="add-service-trigger">
                <SelectValue placeholder="Choisir une prestation" />
              </SelectTrigger>
              <SelectContent className="bg-[#141311] border-[#2C2A26] text-[#F4F0EA]">
                {services.map(s => <SelectItem key={s.id} value={s.id} data-testid={`add-service-opt-${s.id}`}>{s.name} — {s.duration}min · {s.price}€</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Coiffeur</Label>
            <Select value={form.staff_id} onValueChange={(v) => setForm({...form, staff_id: v})}>
              <SelectTrigger className="mt-2 bg-[#0A0908] border-[#2C2A26]" data-testid="add-staff-trigger">
                <SelectValue placeholder="Premier disponible" />
              </SelectTrigger>
              <SelectContent className="bg-[#141311] border-[#2C2A26] text-[#F4F0EA]">
                {(staff || []).map(m => <SelectItem key={m.id} value={m.id} data-testid={`add-staff-opt-${m.id}`}>{m.name}{m.title ? ` — ${m.title}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Date</Label>
              <Input type="date" value={form.date} onChange={(e)=>setForm({...form, date: e.target.value})} className="mt-2 bg-[#0A0908] border-[#2C2A26]" required data-testid="add-date" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Heure</Label>
              <Input type="time" step="900" value={form.start_time} onChange={(e)=>setForm({...form, start_time: e.target.value})} className="mt-2 bg-[#0A0908] border-[#2C2A26]" required data-testid="add-time" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Prénom</Label>
              <Input value={form.client_first_name} onChange={(e)=>setForm({...form, client_first_name: e.target.value})} className="mt-2 bg-[#0A0908] border-[#2C2A26]" required data-testid="add-first" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Nom</Label>
              <Input value={form.client_last_name} onChange={(e)=>setForm({...form, client_last_name: e.target.value})} className="mt-2 bg-[#0A0908] border-[#2C2A26]" required data-testid="add-last" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Téléphone</Label>
            <Input value={form.client_phone} onChange={(e)=>setForm({...form, client_phone: e.target.value})} className="mt-2 bg-[#0A0908] border-[#2C2A26]" required data-testid="add-phone" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Email (optionnel)</Label>
            <Input type="email" value={form.client_email} onChange={(e)=>setForm({...form, client_email: e.target.value})} className="mt-2 bg-[#0A0908] border-[#2C2A26]" data-testid="add-email" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Note interne</Label>
            <Input value={form.internal_note} onChange={(e)=>setForm({...form, internal_note: e.target.value})} className="mt-2 bg-[#0A0908] border-[#2C2A26]" data-testid="add-note" />
          </div>
          <button type="submit" disabled={saving || !form.service_id} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="add-submit">
            {saving ? "Création…" : "Créer le rendez-vous"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
